import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Heart } from "lucide-react";
import { toast, Toaster } from "sonner";
import { Shell } from "@/components/album/ui";
import CoverView from "@/components/album/CoverView";
import Lightbox from "@/components/album/Lightbox";
import {
  compressPair, getAlbum, getGuestName, getPhotos, getQuota, likePhoto, setGuestName, uploadPhotos,
} from "@/lib/api";
import { topFavorites } from "@/lib/favorites";
import { GUEST_QUOTA } from "@/config/album";
import { cn } from "@/lib/utils";

const GOLD = "#C6A56B";

const Message = ({ title, text }) => (
  <Shell className="max-w-md pt-16 text-center">
    <h1 className="font-serif text-4xl font-light">{title}</h1>
    <p className="mt-4 font-sans text-sm text-[#8A8A8A]">{text}</p>
  </Shell>
);

/* ---------- Ambil data album dari server ---------- */
function useAlbum(id) {
  const [state, setState] = useState({ loading: true, album: null, error: null });
  useEffect(() => {
    let live = true;
    getAlbum(id)
      .then((album) => live && setState({ loading: false, album, error: null }))
      .catch((e) => live && setState({ loading: false, album: null, error: e }));
    return () => { live = false; };
  }, [id]);
  return state;
}
const Loading = () => <div className="flex min-h-screen items-center justify-center bg-[#F9F8F6] font-sans text-xs uppercase tracking-[0.4em] text-[#8A8A8A]">Ruang Kenang</div>;
const Failed = ({ error }) =>
  error?.status === 404
    ? <Message title="Album tidak ditemukan" text="Periksa kembali QR atau link yang kamu scan." />
    : <Message title="Tidak bisa dibuka" text={error?.message || "Coba muat ulang halaman."} />;

/* ---------- Halaman utama tamu: intro, cover, isi nama, ambil foto ---------- */
export function GuestHome() {
  const { id } = useParams();
  const nav = useNavigate();
  const { loading, album, error } = useAlbum(id);
  const [intro, setIntro] = useState(true);
  const [left, setLeft] = useState(GUEST_QUOTA);
  const [busy, setBusy] = useState(false);
  const [guest, setGuest] = useState(getGuestName());
  const cam = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setIntro(false), 2600);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    getQuota(id).then((q) => setLeft(q.left)).catch(() => {});
  }, [id]);

  if (loading) return <Loading />;
  if (!album) return <Failed error={error} />;
  if (album.expired) return <Message title="Album sudah berakhir" text="Masa aktif album ini sudah habis." />;

  const onName = (v) => {
    setGuest(v);
    setGuestName(v);
  };

  // Satu jepretan = satu foto = satu permintaan ke server (ringan untuk Cloudflare).
  // Yang dikirim: versi galeri (kecil) + versi ZIP (lebih tajam). Keduanya sudah bernuansa.
  const handle = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (guest.trim().length < 2) return toast.error("Tulis namamu dulu.");
    if (left === 0) return toast.error(`Jatah ${GUEST_QUOTA} fotomu sudah habis.`);
    setBusy(true);
    try {
      const { gallery, zip } = await compressPair(file, album.filter); // nuansa foto dari pemilik acara
      let r;
      try {
        r = await uploadPhotos(id, [gallery], guest.trim(), [zip]);
      } catch (err) {
        // Kiriman terlalu besar: kirim versi galeri saja
        if (err?.status === 413 || err?.status === 400) r = await uploadPhotos(id, [gallery], guest.trim());
        else throw err;
      }
      setLeft(r.left);
      toast.success("Foto masuk ke album");
    } catch (err) {
      toast.error(err?.message || "Foto belum terkirim. Coba lagi.");
      getQuota(id).then((q) => setLeft(q.left)).catch(() => {});
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] text-[#1C1C1C]">
      <Toaster position="top-center" />
      <AnimatePresence>
        {intro && (
          <motion.div key="intro" exit={{ opacity: 0 }} transition={{ duration: 0.8 }} className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-[#F9F8F6]">
            <motion.p initial={{ opacity: 0, letterSpacing: "0.2em" }} animate={{ opacity: 1, letterSpacing: "0.4em" }} transition={{ duration: 1.6 }} className="font-sans text-sm uppercase">Ruang Kenang</motion.p>
            <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 1.2, delay: 0.6 }} className="mt-6 h-px w-24 origin-center bg-[#1C1C1C]" />
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.2 }} className="mt-6 font-serif text-xl italic text-[#5C5C5C]">{album.eventName}</motion.p>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: intro ? 0 : 1 }} transition={{ duration: 1, delay: 0.2 }}>
        <CoverView data={album} left={left} busy={busy} guestName={guest} onName={onName} onCamera={() => cam.current?.click()} onView={() => nav(`/a/${id}/album`)} />
      </motion.div>
      <input ref={cam} type="file" accept="image/*" capture="environment" hidden onChange={handle} />
    </div>
  );
}

/* ---------- Tile foto dengan tombol suka ---------- */
const Tile = ({ p, onOpen, onLike, className, badge }) => (
  <div className={cn("relative overflow-hidden bg-[#EFEBE2]", className)}>
    <button type="button" onClick={onOpen} aria-label="Perbesar foto" className="block h-full w-full">
      <img src={p.url} alt="" loading="lazy" className="h-full w-full object-cover" />
    </button>
    {badge && <span className="pointer-events-none absolute left-1.5 top-1.5 bg-white/85 px-1.5 py-0.5 font-sans text-[10px] text-[#1C1C1C]">{badge}</span>}
    <button
      type="button"
      onClick={() => onLike(p)}
      aria-pressed={p.liked}
      aria-label={p.liked ? "Batal suka" : "Suka"}
      className="absolute bottom-1.5 right-1.5 flex h-8 items-center gap-1 bg-white/85 px-2 font-sans text-[11px]"
    >
      <Heart className="h-4 w-4" style={p.liked ? { fill: GOLD, color: GOLD } : { color: "#8A8A8A" }} />
      {p.likes > 0 && <span>{p.likes}</span>}
    </button>
  </div>
);

/* ---------- Galeri tamu ---------- */
export function GuestGallery() {
  const { id } = useParams();
  const { loading, album, error } = useAlbum(id);
  const [photos, setPhotos] = useState(null);
  const [view, setView] = useState(null); // { pids, index, dir } - daftar dikunci saat dibuka

  useEffect(() => {
    getPhotos(id).then(setPhotos).catch(() => setPhotos([]));
  }, [id]);

  const patch = useCallback((pid, change) => setPhotos((l) => l.map((x) => (x.pid === pid ? { ...x, ...change } : x))), []);
  const like = useCallback(
    async (p) => {
      const on = !p.liked;
      patch(p.pid, { liked: on, likes: Math.max(0, p.likes + (on ? 1 : -1)) });
      try {
        const r = await likePhoto(id, p.pid, on);
        patch(p.pid, { liked: r.liked, likes: r.likes });
      } catch (e) {
        patch(p.pid, { liked: p.liked, likes: p.likes });
        toast.error(e?.message || "Suka belum tersimpan.");
      }
    },
    [id, patch]
  );

  if (loading) return <Loading />;
  if (!album) return <Failed error={error} />;
  if (album.expired) return <Message title="Album sudah berakhir" text="Masa aktif album ini sudah habis." />;

  const top = photos ? topFavorites([...photos]) : [];
  // Urutan dikunci saat dibuka; jumlah suka tetap mengikuti data terbaru
  const open = (list, index) => setView({ pids: list.map((x) => x.pid), index, dir: 0 });
  const live = view ? view.pids.map((pid) => photos.find((q) => q.pid === pid)).filter(Boolean) : [];

  return (
    <Shell back={{ to: `/a/${id}`, label: "Kembali" }} className="max-w-3xl pt-4">
      <Toaster position="top-center" />
      <h1 className="font-serif text-4xl font-light tracking-tight">{album.couple || album.eventName}</h1>
      <p className="mt-2 font-sans text-sm text-[#8A8A8A]">
        {album.allowGuestView ? "Semua foto dari tamu" : "Fotomu di album ini"}{photos ? ` · ${photos.length} foto` : ""}
      </p>
      {photos === null ? (
        <p className="mt-16 text-center font-sans text-sm text-[#8A8A8A]">Memuat…</p>
      ) : photos.length === 0 ? (
        <p className="mt-16 text-center font-sans text-sm text-[#8A8A8A]">Belum ada foto. <Link to={`/a/${id}`} className="underline">Ambil foto pertama</Link></p>
      ) : (
        <>
          {top.length > 0 && (
            <section className="mt-8" aria-label="Foto favorit">
              <h2 className="font-serif text-2xl font-light">Favorit</h2>
              <div className="mt-1 h-px w-12" style={{ background: GOLD }} />
              <div className="mt-4 grid grid-cols-4 gap-1.5">
                {top.map((p, i) => (
                  <Tile
                    key={p.pid}
                    p={p}
                    badge={i === 0 ? "Paling disukai" : null}
                    className={i === 0 ? cn("col-span-2 row-span-2", top.length < 3 && "aspect-square") : "aspect-square"}
                    onOpen={() => open(top, i)}
                    onLike={like}
                  />
                ))}
              </div>
            </section>
          )}
          <div className="mt-8 grid grid-cols-3 gap-1.5">
            {photos.map((p, i) => (
              <Tile key={p.pid} p={p} className="aspect-square" onOpen={() => open(photos, i)} onLike={like} />
            ))}
          </div>
        </>
      )}

      {view && live.length > 0 && (
        <Lightbox
          photos={live}
          index={Math.min(view.index, live.length - 1)}
          direction={view.dir}
          onIndex={(index, dir) => setView((v) => ({ ...v, index, dir }))}
          onClose={() => setView(null)}
          onLike={like}
        />
      )}
    </Shell>
  );
}
